import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Calculator, Stethoscope } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { todayIso, toIsoDate } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const monthStart = () => {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

export function ComputePayoutPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/accounting/payouts');
  const [doctorId, setDoctorId] = useState('');
  const [periodStart, setPeriodStart] = useState(monthStart());
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [deductions, setDeductions] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const doctors = useQuery({
    queryKey: ['doctors'],
    queryFn: async () => (await get<{ data: { id: string; name: string }[] }>('/org/clinicians?kind=doctors&limit=200')).data,
  });

  const compute = useMutation({
    mutationFn: () =>
      post('/accounting/payouts/compute', {
        doctorId,
        periodStart,
        periodEnd,
        deductionsMinor: minorFromInput(deductions),
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'payout_computed'), tr('billing', 'recomputing_the_same_period_replaces_the_draft'));
      void queryClient.invalidateQueries({ queryKey: ['payouts'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !compute.isSuccess && (Boolean(doctorId) || deductions !== '0' || periodStart !== monthStart() || periodEnd !== todayIso());

  return (
    <FormScreen
      title={tr('billing', 'compute_a_payout')}
      description={tr('billing', 'only_collected_money_is_shared_from_the_patient')}
      eyebrow={tr('billing', 'doctor_payouts')}
      icon={Calculator}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!doctorId) return;
        setError(null);
        compute.mutate();
      }}
    >
      <FormSection title={tr('billing', 'doctor')} icon={Stethoscope}>
        <Field label={tr('billing', 'doctor')} required className="sm:col-span-2">
          <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">{tr('billing', 'select')}</option>
            {doctors.data?.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'period')} icon={CalendarRange}>
        <Field label={tr('billing', 'from')} required>
          <DatePicker value={periodStart} onChange={setPeriodStart} />
        </Field>
        <Field label={tr('billing', 'to_2')} required>
          <DatePicker value={periodEnd} onChange={setPeriodEnd} />
        </Field>
        <Field label={tr('billing', 'deductions')} hint={tr('billing', 'advances_clinic_charges_withholding')}>
          <Input type="text" dir="ltr" inputMode="decimal" value={deductions} onChange={(e) => setDeductions(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'compute')} loading={compute.isPending} incomplete={!doctorId} cancelTo={returnTo} />
    </FormScreen>
  );
}
